/**
 * Die Vorlese-Reihe der Entwickler-Beiträge (20.10.2026, Martin: „zwei Fenster standen auf wird vorgelesen“).
 *
 * EINE Reihe für alle Sprecher (Schmied, Zimmermann, Hüter) in Ankunftsreihenfolge. Ein Eintrag trägt „unvorgelesen“, bis er an der
 * Reihe ist, dann „wird vorgelesen“ (immer nur einer), danach „vorgelesen“ oder — blieb er still — „unvorgelesen“.
 *
 * ⚠️ 21.10.2026, 07:12–07:14 (Martin: Mikro ging nach Zimmermanns Frage nicht auf) — DAS MIKRO WARTET AUF DAS ENDE DER GANZEN REIHE.
 *    Jeder Entwickler hat ein eigenes Warten (lib/schmied-warten.ts); es zählt nur SEIN Vorlesen. Schmieds Mikro wollte darum nach
 *    seinem Beitrag öffnen, obwohl Zimmermann noch anstand — im Ausklang begann dessen Stimme (`[ptt] nach-frage übersprungen
 *    wiedergabe`), und das Mikro war verloren. Jetzt: läuft die Reihe noch (`offen() > 0`), merkt `mikroNachReihe` den Versuch und
 *    ruft ihn GENAU EINMAL, wenn der letzte Eintrag fertig ist (nach seinem `danach`). Ein neuerer Versuch ersetzt den älteren — auch
 *    einer, der bei leerer Reihe sofort öffnet (das neueste gewinnt, keine Doppelöffnung). Ein Stopp verwirft ihn (`verwirfMikroNachReihe`).
 *    Ob es dann wirklich aufgeht (Zug, Stufe, Ruhezustand, Vordergrund, Tippen), prüft der Versuch selbst (app.ts).
 *
 * ⚠️ 28.10.2026 (Martin 07:54: „Das Ganze braucht eine logische Abfolge.“, shared/stimme-konto.ts „BESITZ“) — FESTE REIHENFOLGE
 *    (nur mit `grenze`, Server-Schalter REIHENFOLGE_WIEDERGABE): eine Ja/Nein-Frage an Martin (`vorrang`) rückt VOR die wartenden
 *    Berichte — nie vor den, der gerade spricht (kein Abbruch mitten im Satz). Es warten höchstens `anzahl` Berichte; ein weiterer
 *    bleibt Blase. Wer länger als `wartenMs` wartete, bleibt ebenfalls Blase (er kommt nicht Minuten später zur Unzeit); der
 *    Sprech-Aufruf bekommt die Restzeit (`sprich(restMs)`), damit auch sein Warten auf Stille nicht länger dauert. Eine Frage an
 *    Martin (`vorrang`) ist von der Frist ausgenommen (`restMs` = `null`) — sie wird noch gesprochen und öffnet danach das Mikro.
 */
import { zeigeVorlesenStand } from "./entwickler-blase.js";

export type VorleseErgebnis = "ja" | "anderswo" | "nein";

/** Die feste Reihenfolge (28.10.2026): höchstens `anzahl` wartende Berichte, höchstens `wartenMs` Wartezeit. */
export interface VorleseGrenze {
  anzahl: number;
  wartenMs: number;
}

export interface VorleseReihe {
  /**
   * `vorrang`: eine Ja/Nein-Frage an Martin — sie rückt vor die wartenden Berichte (nie vor den sprechenden) und fällt nie der
   * Grenze zum Opfer. `sprich(restMs)`: `restMs` ist die Restzeit bis zur Wartegrenze (ohne Grenze und für eine Frage `null`).
   */
  einreihen: (o: {
    blase: HTMLElement | null;
    sprich: (restMs: number | null) => Promise<VorleseErgebnis>;
    danach?: (e: VorleseErgebnis) => void;
    vorrang?: boolean;
  }) => void;
  /** Wie viele Einträge noch nicht fertig sind (wartend oder sprechend) — im `danach` eines Eintrags zählt er selbst nicht mehr. */
  offen: () => number;
  /**
   * Läuft die Reihe noch: `versuch` nach ihrem Ende genau einmal (ersetzt einen älteren) — `true`. Ist sie leer: `false`, und ein
   * älterer gemerkter Versuch verfällt (der Aufrufer öffnet jetzt selbst, das neueste gewinnt).
   */
  mikroNachReihe: (versuch: () => void) => boolean;
  /** Stopp, Sprechtaste, „Fertig“: einen gemerkten Versuch verwerfen. `true`, wenn einer wartete. */
  verwirfMikroNachReihe: () => boolean;
  /** Restzeit bis zur Wartegrenze des Eintrags, der gerade dran ist — `null` ohne Grenze, für eine Frage oder wenn keiner dran ist. */
  restMs: () => number | null;
}

interface Eintrag {
  blase: HTMLElement | null;
  sprich: (restMs: number | null) => Promise<VorleseErgebnis>;
  danach?: ((e: VorleseErgebnis) => void) | undefined;
  vorrang: boolean;
  seit: number;
}

export function erzeugeVorleseReihe(o: {
  /** Die Grenze, oder `null` (Schalter aus): dann wie vor dem 28.10.2026 — Ankunftsreihenfolge, keine Grenze. */
  grenze?: () => VorleseGrenze | null;
  jetzt?: () => number;
  /** Journalzeile (nur Zahlen, nie Inhalt). */
  melde?: (zeile: string) => void;
} = {}): VorleseReihe {
  const jetzt = o.jetzt ?? Date.now;
  const wartend: Eintrag[] = [];
  let spricht = false;
  /** Der Eintrag, der gerade dran ist (für `restMs`). */
  let dran: Eintrag | null = null;
  let offen = 0;
  let nachReihe: (() => void) | null = null;

  const grenzeJetzt = (): VorleseGrenze | null => {
    try {
      return o.grenze?.() ?? null;
    } catch {
      return null;
    }
  };
  const melde = (zeile: string): void => {
    try {
      o.melde?.(zeile);
    } catch {
      /* Journal ist Beiwerk */
    }
  };

  /** Ein Eintrag ist fertig (gesprochen, still, verworfen): Stand, `danach`, ggf. das Mikro nach der Reihe. */
  const abschliessen = (e: Eintrag, ergebnis: VorleseErgebnis): void => {
    offen = Math.max(0, offen - 1);
    try {
      // „anderswo“: eine andere Seite hat ihn gesprochen — gehört, aber nicht hier.
      if (e.blase) zeigeVorlesenStand(e.blase, ergebnis === "nein" ? "nicht-vorgelesen" : "vorgelesen");
      e.danach?.(ergebnis);
    } finally {
      // Die Reihe ist leer: ein verschobenes Mikro genau einmal (das `danach` oben kann es schon ersetzt/eingelöst haben).
      if (offen === 0 && nachReihe) {
        const versuch = nachReihe;
        nachReihe = null;
        versuch();
      }
    }
  };

  /**
   * Restzeit bis zur Wartegrenze — `null` ohne Grenze UND für eine Frage an Martin (`vorrang`, 28.10.2026, Prüfbefund M1): sie fällt
   * nie der 90-s-Frist zum Opfer (sonst bliebe sie stumm und ihr Mikro zu, wenn Vespera oder Martins Aufnahme länger dauerte); ihr
   * Warten auf Stille hat dann wie vor der Reihenfolge nur die Obergrenze des Aufrufers (app.ts SCHMIED_STILLE_MAX_MS).
   */
  const fristRest = (g: VorleseGrenze | null, e: Eintrag): number | null => (g && !e.vorrang ? e.seit + g.wartenMs - jetzt() : null);

  const naechster = (): void => {
    if (spricht || wartend.length === 0) return;
    // Mit Grenze: die älteste Frage an Martin vor den Berichten; sonst (und ohne Grenze) die Ankunftsreihenfolge.
    const g = grenzeJetzt();
    const i = g ? Math.max(0, wartend.findIndex((w) => w.vorrang)) : 0;
    const [e] = wartend.splice(i, 1);
    if (!e) return;
    spricht = true;
    dran = e;
    void Promise.resolve()
      .then(() => {
        const rest = fristRest(g, e);
        if (rest !== null && rest <= 0) {
          melde(`[vorlese-reihe] nur blase grund=frist gewartet=${Math.round((jetzt() - e.seit) / 1000)}s`);
          return "nein" as const;
        }
        if (e.blase) zeigeVorlesenStand(e.blase, "steht-an");
        return e.sprich(rest);
      })
      .catch(() => "nein" as const)
      .then((ergebnis) => {
        try {
          abschliessen(e, ergebnis);
        } finally {
          spricht = false;
          dran = null;
          naechster();
        }
      })
      // Ein Wurf im „danach“-Aufruf darf die Reihe nicht stumm abbrechen: der nächste Eintrag kommt trotzdem dran.
      .catch(() => {});
  };

  return {
    einreihen({ blase, sprich, danach, vorrang }): void {
      const g = grenzeJetzt();
      const frage = vorrang === true;
      // Feste Reihenfolge: höchstens `anzahl` Berichte warten; ein weiterer bleibt Blase (Fragen an Martin zählen nicht).
      if (g && !frage && wartend.filter((w) => !w.vorrang).length >= g.anzahl) {
        melde(`[vorlese-reihe] nur blase grund=voll wartend=${wartend.length}`);
        if (blase) zeigeVorlesenStand(blase, "nicht-vorgelesen");
        try {
          danach?.("nein");
        } catch {
          /* der Aufrufer darf die Reihe nicht stören */
        }
        return;
      }
      if (blase) zeigeVorlesenStand(blase, "wartet");
      offen++;
      wartend.push({ blase, sprich, danach, vorrang: frage, seit: jetzt() });
      if (g && frage && (spricht || wartend.length > 1)) melde(`[vorlese-reihe] frage vorn wartend=${wartend.length - 1}`);
      naechster();
    },
    offen: () => offen,
    restMs(): number | null {
      const g = grenzeJetzt();
      return dran ? fristRest(g, dran) : null;
    },
    mikroNachReihe(versuch): boolean {
      if (offen === 0) {
        nachReihe = null;
        return false;
      }
      nachReihe = versuch;
      return true;
    },
    verwirfMikroNachReihe(): boolean {
      const war = nachReihe !== null;
      nachReihe = null;
      return war;
    },
  };
}
